// PWA (Progressive Web App) utilities
// Handles service worker registration and install prompts

let deferredPrompt = null;

/**
 * Check if the current device is a mobile device
 * @returns {boolean} True if mobile device with touch capability
 */
export function isMobileDevice() {
  // Check for actual mobile devices, not just screen size
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobileUserAgent = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  
  // Check if it's a touch device
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Only consider it mobile if it has mobile user agent AND touch capability
  // This prevents desktop browsers with small windows from being treated as mobile
  return isMobileUserAgent && isTouchDevice;
}

/**
 * Show the PWA install promotion banner
 */
function showInstallPromotion() {
  // Create a better-designed banner for PWA installation
  const banner = document.createElement('div');
  banner.style.cssText = `
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    background: linear-gradient(135deg, #1557b0 0%, #2196F3 100%);
    color: white;
    padding: 12px 16px;
    z-index: 999;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    border-bottom: 1px solid rgba(255,255,255,0.1);
  `;
  
  banner.innerHTML = `
    <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
      <div style="
        width: 36px; 
        height: 36px; 
        background: white; 
        border-radius: 8px; 
        display: flex; 
        align-items: center; 
        justify-content: center;
        font-size: 18px;
        flex-shrink: 0;
      ">
        🧳
      </div>
      <div style="flex: 1; min-width: 0;">
        <div style="font-weight: 600; font-size: 14px; margin-bottom: 2px;">
          Install Trip Planner
        </div>
        <div style="font-size: 12px; opacity: 0.9; line-height: 1.2;">
          Get offline access and faster loading
        </div>
      </div>
      <button id="dismiss-btn" style="
        background: transparent; 
        border: none; 
        color: white; 
        font-size: 18px; 
        padding: 4px; 
        cursor: pointer; 
        opacity: 0.7;
        border-radius: 50%;
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      ">×</button>
    </div>
    <div style="display: flex; gap: 8px;">
      <button id="install-btn" style="
        flex: 1;
        padding: 6px 12px; 
        background: white; 
        color: #1557b0; 
        border: none; 
        border-radius: 6px; 
        cursor: pointer;
        font-weight: 600;
        font-size: 13px;
        transition: all 0.2s ease;
        height: 32px;
      ">
        Install
      </button>
      <button id="later-btn" style="
        padding: 6px 10px; 
        background: transparent; 
        color: white; 
        border: 1px solid rgba(255,255,255,0.4); 
        border-radius: 6px; 
        cursor: pointer;
        font-size: 12px;
        opacity: 0.8;
        min-width: 50px;
        height: 32px;
      ">
        Later
      </button>
    </div>
  `;
  
  document.body.appendChild(banner);
  
  // Add hover effects
  const installBtn = document.getElementById('install-btn');
  const laterBtn = document.getElementById('later-btn');
  const dismissBtn = document.getElementById('dismiss-btn');
  
  installBtn.addEventListener('mousedown', () => {
    installBtn.style.transform = 'scale(0.98)';
  });
  installBtn.addEventListener('mouseup', () => {
    installBtn.style.transform = 'scale(1)';
  });
  
  dismissBtn.addEventListener('mouseover', () => {
    dismissBtn.style.opacity = '1';
    dismissBtn.style.background = 'rgba(255,255,255,0.1)';
  });
  dismissBtn.addEventListener('mouseout', () => {
    dismissBtn.style.opacity = '0.7';
    dismissBtn.style.background = 'transparent';
  });
  
  // Handle install button click
  installBtn.addEventListener('click', () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('PWA: User accepted the install prompt');
        } else {
          console.log('PWA: User dismissed the install prompt');
        }
        deferredPrompt = null;
        banner.remove();
      });
    }
  });
  
  // Handle later and dismiss button clicks
  const removeBanner = () => banner.remove();
  laterBtn.addEventListener('click', removeBanner);
  dismissBtn.addEventListener('click', removeBanner);
  
  // Auto-dismiss after 15 seconds
  setTimeout(() => {
    if (banner.parentNode) {
      banner.remove();
    }
  }, 15000);
}

/**
 * Register the service worker for PWA functionality
 */
export function registerServiceWorker() {
  // Register service worker for PWA functionality
  // Temporarily disabled to prevent endless loops
  // First, unregister any existing service worker that might be causing loops
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(function(registrations) {
      for(let registration of registrations) {
        registration.unregister().then(function(boolean) {
          console.log('PWA: Service worker unregistered:', boolean);
        });
      }
    });
  }

  /*
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('PWA: Service worker registered successfully:', registration.scope);
          
          // Check for updates only once
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // New content is available, prompt user to refresh
                  console.log('PWA: New service worker installed, update available');
                  // Don't auto-prompt, just log for now to prevent loops
                  // if (window.confirm('New version available! Refresh to update?')) {
                  //   window.location.reload();
                  // }
                }
              });
            }
          });
        })
        .catch((error) => {
          console.log('PWA: Service worker registration failed:', error);
        });
    });
  }
  */
}

/**
 * Setup PWA install prompt handling
 */
export function setupInstallPrompt() {
  // Add to home screen prompt for PWA
  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent Chrome 67 and earlier from automatically showing the prompt
    e.preventDefault();
    // Stash the event so it can be triggered later
    deferredPrompt = e;
    
    // Show custom install button or banner only on mobile
    console.log('PWA: Install prompt available');
    
    // Check if it's a mobile device before showing install promotion
    if (isMobileDevice()) {
      showInstallPromotion();
    }
  });
}

/**
 * Initialize all PWA functionality
 * Call this function from your main app entry point
 */
export function initializePWA() {
  console.log('PWA: Initializing Progressive Web App features...');
  
  // Register service worker
  registerServiceWorker();
  
  // Setup install prompt handling
  setupInstallPrompt();
  
  console.log('PWA: Initialization complete');
}

// Export individual functions for granular control
export { showInstallPromotion };
